import { DiagramComponent } from '../../core/DiagramComponent';
import type { BaseComponentConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

interface GeometricGridConfig extends BaseComponentConfig {
  type: 'geometricgrid';
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
  step?: number;
  color?: string;
  width?: number;
  showPoints?: boolean;
  attributes?: Record<string, any>;
}

/**
 * Grid component - creates a grid of points or lines
 */
export class Grid extends DiagramComponent<GeometricGridConfig> {
  constructor(config: GeometricGridConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
  }

  render(): void {
    const config = this.config;
    const xMin = config.xMin || -5;
    const xMax = config.xMax || 5;
    const yMin = config.yMin || -5;
    const yMax = config.yMax || 5;
    const step = config.step || 1;

    const ensureCssClass = (existing: string | undefined, cls: string): string => {
      if (!existing || existing.trim().length === 0) {
        return cls;
      }
      return existing.split(/\s+/).includes(cls) ? existing : `${existing} ${cls}`;
    };

    const attributes: Record<string, any> = {
      strokeColor: config.color || '#ccc',
      strokeWidth: config.width || 1,
      fixed: true,
      highlight: false,
      layer: 0, // Put grid on background layer
      ...config.attributes,
    };
    attributes.cssClass = ensureCssClass(attributes.cssClass, 'jxg-grid-line');

    // Vertical lines
    for (let x = xMin; x <= xMax; x += step) {
      const line = this.board.create('line', [[x, yMin], [x, yMax]], {
        ...attributes,
        straightFirst: false,
        straightLast: false,
      });
      this.jsxElements.push(line);
    }

    // Horizontal lines
    for (let y = yMin; y <= yMax; y += step) {
      const line = this.board.create('line', [[xMin, y], [xMax, y]], {
        ...attributes,
        straightFirst: false,
        straightLast: false,
      });
      this.jsxElements.push(line);
    }

    // Optionally create intersection points
    if (config.showPoints) {
      for (let x = xMin; x <= xMax; x += step) {
        for (let y = yMin; y <= yMax; y += step) {
          const point = this.board.create('point', [x, y], {
            size: 1,
            fillColor: config.color || '#999',
            strokeColor: config.color || '#999',
            fixed: true,
            cssClass: ensureCssClass(undefined, 'jxg-grid-point'),
          });
          this.jsxElements.push(point);
        }
      }
    }
  }

  /**
   * Grid never needs to update - override to prevent destroy/recreate
   */
  protected _rerender(): void {
    // Grid is static, never update it
    if (this.jsxElements.length === 0) {
      this.render();
    }
  }

  getSpecificity(): number {
    return 5; // Grid has low priority (background element)
  }
}
